import React from "react";
import {
  ShieldCheck, FileText, ClipboardCheck, Search, Send,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  FeatureCallout, AgentCard, ChecklistCard, StatBar,
  FlowDiagram, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogElenaPreSubmissionScrubbing = () => (
  <RichBlogLayout
    category="Revenue Cycle Management"
    eyebrow="Claim Scrubbing"
    title="Elena: Pre-Submission Claim Scrubbing That Stops Denials Before They Happen"
    subtitle="Every claim, scrubbed against 15,000+ historical data points — staged for review the moment something looks wrong."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        It's 4:38 PM on a Thursday and the biller has 47 claims still
        sitting in the unsent queue. Five of them — D2750 crowns, two
        SRPs, a D6010 implant — are the ones that actually need eyes on
        them. The rest will go out with a glance and a prayer. Three
        weeks from now, the denials from this batch will start rolling
        back in: a missing X-ray on tooth #14, a narrative that read like
        a template, a frequency violation nobody checked.
      </p>
      <p>
        None of those denials will be a surprise. They're the same handful
        of mistakes the industry repeats thousands of times a day —
        missing radiographs, thin narratives for major procedures,
        tooth-to-surface mappings that don't reconcile, codes that the
        carrier downgrades by default.
      </p>
      <p>
        Each one is small. Each one is, individually, predictable. The
        problem is that catching them manually — on every claim, every
        day — is a job no human team can keep up with.
      </p>
    </Prose>

    <AgentCard
      name="Elena"
      role="AI Claim Scrubber · Pre-Submission QA"
      initial="E"
      description="Elena scrubs every claim before it gets submitted, against more than 15,000 historical data points of what tends to get denied and why. When she finds something off, she doesn't just flag it — she stops the claim from going out and stages it for review with full context."
      channels={["Pre-submission scrub", "Documentation check", "Code review", "Stage for review"]}
    />

    <Prose>
      <h2>Most denials are not surprises</h2>
      <p>
        A typical practice has a 5–10% denial rate, and a good chunk of
        those are first-pass denials that could have been prevented at
        submission. Each denial costs $25–50 in staff time to research,
        appeal, resubmit, and follow up on — and pushes payment 30–60
        days into the future.
      </p>
      <p>
        Multiply that across a year and the cost of preventable denials
        is real money — both in cash flow and in the time of the
        billing team that could be working aging claims instead of
        rework.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 100, suffix: "%", label: "Of claims scrubbed pre-submission" },
        { value: 15, suffix: "k+", label: "Historical data points referenced" },
        { value: 0, label: "Claims sent without a full review" },
        { value: 30, suffix: " days", label: "Avg cash-flow lift from clean first-pass" },
      ]}
    />

    <Prose>
      <p>
        Elena turns the bottleneck inside out. Instead of a biller
        carefully reviewing the handful of claims they have time for,
        every claim — without exception — is reviewed in detail before
        it leaves the system.
      </p>

      <h2>Why a clearinghouse edit isn't a scrub</h2>
      <p>
        Most clearinghouses do format checks and basic edits — making
        sure the fields are filled in and the syntax is right. That's
        the floor. Elena does the actual clinical and operational
        review: are the X-rays attached, is the narrative real, is the
        frequency valid for this patient on this plan, is the code
        correct for the work that was done.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="Manual claim review"
      afterTitle="With Elena"
      beforeItems={[
        "Biller reviews the handful of claims they can fit in a day",
        "The rest go out with a quick scan, hoping for the best",
        "Denials surface 2–4 weeks later, after the visit is long gone",
        "Rework cycle: research, fix, appeal, follow-up, resubmit",
      ]}
      afterItems={[
        "Every claim scrubbed against 15,000+ data points before submission",
        "Anything risky is staged in your unsent queue with context",
        "Issues fixed before the claim ever leaves your system",
        "Cleaner first-pass acceptance, faster reimbursements",
      ]}
    />

    <Prose>
      <h2>Every check that runs before submission</h2>
      <p>
        For a single typical claim — say, a D2750 crown — Elena runs a
        set of checks that mirror what a great biller would do, given
        unlimited time. The difference is that she does it on every
        claim, every time.
      </p>
    </Prose>

    <ChecklistCard
      title="Pre-submission scrub · D2750 crown · Maria L."
      items={[
        { label: "Pre-op X-rays attached and dated", status: "pass" },
        { label: "Periapical or BWX showing tooth #19", status: "pass" },
        { label: "Narrative present and procedure-specific", status: "pass" },
        { label: "Tooth-to-surface mapping consistent with code", status: "pass" },
        { label: "Frequency limit not exceeded for major", status: "pass" },
        { label: "Waiting period satisfied per plan rules", status: "running" },
        { label: "Downgrade clause check (porcelain → metal)", status: "fail" },
        { label: "Contracted fee schedule applied correctly", status: "pass" },
      ]}
    />

    <Prose>
      <p>
        On this claim, the downgrade clause check failed — the plan
        downgrades porcelain to metal, and the claim was prepared at the
        full porcelain rate. Elena stages the claim, attaches the rule,
        and surfaces it to the biller before it goes out. Two minutes of
        review prevents what would have otherwise been a 30-day denial
        cycle.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Intercepts every claim before submission",
          body:
            "As each claim is prepared, Elena pulls it into a pre-submission queue and runs her review in real time — before the clearinghouse, before the carrier.",
        },
        {
          title: "Checks against 15,000+ historical data points",
          body:
            "Elena knows what tends to get denied and why. She references prior denials, payer-specific quirks, plan rules, and code-level documentation expectations.",
        },
        {
          title: "Validates documentation",
          body:
            "Are the X-rays attached? Is the narrative real and procedure-specific? Are the perio charts present for the SRPs? Elena confirms documentation isn't just present, but appropriate.",
        },
        {
          title: "Checks coding and consistency",
          body:
            "Tooth-to-surface mapping. Code-to-narrative match. Frequency violations. Waiting periods. Downgrade clauses. Each one validated.",
        },
        {
          title: "Stops at-risk claims, stages them for review",
          body:
            "If something is off, Elena doesn't push the claim through. She stages it in your unsent queue with a clear note on what to fix and why.",
        },
        {
          title: "Lets clean claims sail through",
          body:
            "Anything that passes the scrub goes out the door clean — and the team only spends time on the claims that actually need their attention.",
        },
      ]}
    />

    <PullQuote>
      The denials Elena catches are the ones the team would have caught
      themselves — three weeks too late, after the visit is long gone.
    </PullQuote>

    <Prose>
      <h2>What this looks like across a week</h2>
      <p>
        Below is a week of Elena's work in a typical practice — the
        breakdown of what got staged for review and why. The vast
        majority of claims sail through. The ones that don't are the
        exact denials the team would have caught — three weeks too late.
      </p>
    </Prose>

    <StatBar
      title="Elena's week · staged-for-review reasons"
      rows={[
        { label: "Missing or weak narrative", value: 12, max: 30 },
        { label: "X-ray attachment missing", value: 9, max: 30 },
        { label: "Frequency or waiting violation", value: 7, max: 30 },
        { label: "Downgrade clause not applied", value: 6, max: 30 },
        { label: "Tooth-to-surface mismatch", value: 4, max: 30 },
        { label: "Code-to-narrative inconsistency", value: 3, max: 30 },
      ]}
    />

    <Prose>
      <h2>The unsent claims queue, reframed</h2>
      <p>
        For most teams, the unsent claims queue is a black hole. Things
        sit there because someone forgot, because something is missing,
        because nobody got back to it. With Elena, the unsent queue
        becomes a curated work list: every entry is there for a specific,
        named reason, with the fix already suggested.
      </p>
      <p>
        Reviewing it stops being archaeology and becomes a series of
        small, fast decisions. Most are 30-second fixes. Some need a
        provider or scheduler in the loop. None of them require digging.
      </p>
    </Prose>

    <FlowDiagram
      title="Where Elena fits in the claim lifecycle"
      nodes={[
        { label: "Claim prepared", sublabel: "Coding done", icon: FileText },
        { label: "Elena scrubs", sublabel: "15k+ checks", icon: Search },
        { label: "Stage or pass", sublabel: "Decision", icon: ClipboardCheck },
        { label: "Clean submission", sublabel: "Carrier-ready", icon: Send },
      ]}
    />

    <FeatureCallout
      stat="Every claim"
      label="Scrubbed before submission, no exceptions"
      description="Elena doesn't review a sample. She reviews the whole queue — every claim, every day — and only the ones that need a human reach the team."
      icon={ShieldCheck}
    />

    <Prose>
      <h2>What's left for the billing team</h2>
      <p>
        The billing team stops being the rework department. Less time
        researching denials means more time working aging accounts,
        chasing real underpayments, and managing patient balances. The
        revenue cycle gets faster because fewer claims are bouncing.
        Cash comes in sooner, and the team spends their day on the work
        that actually matters.
      </p>
      <p>
        For the practice owner, the headline is simpler: more first-pass
        clean submissions, fewer denials, fewer surprises in the AR aging
        report.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Most denials should never have left the building. Elena makes
        sure they don't — every claim, scrubbed in detail, with the
        risky ones held back for a quick human decision before they
        ever reach the carrier.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogElenaPreSubmissionScrubbing;
